import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { AtomIcon, ArrowRightIcon } from "@phosphor-icons/react";

// Colour per family so the pivot is visually distinct on the mini-trend.
const FAMILY_COLOR = {
  ORB: "#F97316", // orange = legacy stack
  MR:  "#60A5FA",
  TF:  "#22C55E",
  VB:  "#D6FF38",
  SR:  "#A78BFA",
};

const CHART_MARGIN = { top: 6, right: 6, left: 0, bottom: 0 };
const TOOLTIP_STYLE = {
  background: "#15171E", border: "1px solid #2A2D3A",
  borderRadius: 6, fontSize: 12,
};

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function FamilyProgressionCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/family-progress?limit=30")
      .then((r) => setData(r.data))
      .catch(() => setData({ families: [], history: [] }));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">
          Family Progression
        </div>
        <div className="mt-6 text-xs text-[#64748B] mono">loading…</div>
      </div>
    );
  }

  const families = data.families || [];
  const history = (data.history || []).map((snap) => ({
    cycle: snap.cycle,
    ORB: snap.counts?.ORB ?? 0,
    MR:  snap.counts?.MR ?? 0,
    TF:  snap.counts?.TF ?? 0,
    VB:  snap.counts?.VB ?? 0,
    SR:  snap.counts?.SR ?? 0,
    deltas: snap.backtest_deltas ?? 0,
  }));

  // Highlight the four new families first, then ORB (legacy) after.
  const orderedCodes = ["MR", "TF", "VB", "SR", "ORB"];
  const bar = orderedCodes.map((code) => {
    const row = families.find((f) => f.code === code);
    return {
      code,
      label: row?.label || code,
      total: row?.total || 0,
      active: row?.active || 0,
      testing: row?.testing || 0,
      champion: row?.champion || 0,
      elite: row?.elite || 0,
      basic: row?.basic || 0,
      color: FAMILY_COLOR[code] || "#94A3B8",
    };
  });

  const lastSnap = history.length ? history[history.length - 1] : null;
  const latestDeltas = lastSnap?.deltas ?? 0;

  return (
    <div
      data-testid="family-progression-card"
      className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AtomIcon size={14} className="text-[#D6FF38]" weight="duotone" />
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">
            Family Progression · Diversification Pivot
          </div>
        </div>
        <Link
          to="/strategy-leaders"
          data-testid="family-progression-link"
          className="text-xs mono uppercase tracking-widest text-[#D6FF38] flex items-center gap-1 hover:gap-2 transition-all"
        >
          strategy leaders <ArrowRightIcon size={12} />
        </Link>
      </div>

      {/* Family count tiles (new strategies first, ORB last for contrast). */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {bar.map((f) => (
          <div
            key={f.code}
            data-testid={`family-tile-${f.code}`}
            className="rounded border p-2.5"
            style={{
              borderColor: `${f.color}44`,
              background: "rgba(21,23,30,0.6)",
            }}
          >
            <div className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">
              {f.code}
            </div>
            <div
              className="mono text-2xl font-medium tracking-tight mt-1"
              style={{ color: f.color }}
              data-testid={`family-count-${f.code}`}
            >
              {f.total}
            </div>
            <div className="text-[10px] mono text-[#64748B] mt-0.5 truncate" title={f.label}>
              {f.label}
            </div>
            <div className="text-[10px] mono text-[#CBD5E1] mt-1">
              <span style={{ color: f.color }}>{f.active}</span> active
              {f.champion ? <span> · {f.champion}★</span> : null}
            </div>
          </div>
        ))}
      </div>

      {/* Mini-trend across recent cycles. Empty state message when no
          leaderboard has been ingested yet. */}
      <div className="flex items-center justify-between text-[11px] mono text-[#94A3B8] mb-2">
        <span>Last {history.length || 0} cycles · {latestDeltas} bots moved last push</span>
        <span>synced {timeAgo(data.last_synced_at)}</span>
      </div>
      <div className="h-40">
        {history.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs mono text-[#64748B]">
            no leaderboard cycles yet — waiting for bridge push
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={history} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="2 4" stroke="#2A2D3A" opacity={0.4} />
              <XAxis
                dataKey="cycle"
                tickLine={false}
                axisLine={{ stroke: "#2A2D3A" }}
                tick={{ fill: "#64748B", fontSize: 10 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={30}
                tick={{ fill: "#64748B", fontSize: 10 }}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend
                wrapperStyle={{ fontSize: 10, color: "#94A3B8" }}
                iconType="circle"
                iconSize={7}
              />
              {orderedCodes.map((code) => (
                <Line
                  key={code}
                  type="monotone"
                  dataKey={code}
                  stroke={FAMILY_COLOR[code]}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
